import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type {
  Button,
  IButtonProps,
  IContextualMenuProps,
  BaseButton,
} from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { memoizeFunction } from "@fluentui/react";
import type { ITheme } from "@fluentui/react/lib/Styling";

import {
  dataUniqueId,
  getPreventMenuClickCallback,
  isIdleCallbackEnabled,
  uniqueDropdownId,
} from "@1js/acui-common";
import type { AppContextType } from "@1js/acui-context";
import { AppContext } from "@1js/acui-context";
import {
  checkExperiment,
  ribbonExperiments,
  useMinimumTargetSizesIsEnabled,
} from "@1js/acui-experiments";
import { getDownChevronIcon, iconNameToIconProps } from "@1js/acui-icons";
import type {
  ControlInMenuRendererFunction,
  MenuDefinition,
} from "@1js/acui-menu";
import {
  AppContextualMenu,
  convertMenuDefinitionToFabricMenuProps,
  DefaultMenuAnchorContext,
  emptyMenuProps,
  MenuAnchorContext,
} from "@1js/acui-menu";
import {
  getShadowRenderingContextValue,
  getShadowRenderMenuValue,
  ShadowRenderingContext,
} from "@1js/acui-shadow-rendering";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppTooltipHost, getTooltipText } from "@1js/acui-tooltip";

import { setTooltipsOnMenuItems, tabsterAttributeName } from "../../utilities";
import { deriveButtonKeytip } from "@1js/acui-keytip";
import { AppBaseButton, getLabelButtonProps } from "../AppBaseButton";
import {
  getClassNamesForButton,
  getSplitButtonClassNames,
} from "./AppSplitButton.classNames";
import type { AppSplitButtonInternalProps } from "./AppSplitButton.Props";

interface AppSplitButtonState {
  // Whether the menu has been opened at least once after mounting
  menuHasBeenOpened?: boolean;
  // Whether the menu is in the shadow rendering phase.
  isShadowRendering?: boolean;
}

export class AppSplitButton extends React.PureComponent<
  AppSplitButtonInternalProps,
  AppSplitButtonState
> {
  private baseButton = React.createRef<AppBaseButton>();
  private tooltipHost: AppTooltipHost | undefined;
  private tooltipHostMap: Map<string, AppTooltipHost> = new Map();
  private initiatedShadowRendering = false;
  // We will default to a generic ID and set a specific Id with the
  // custom menu Id when we render
  private splitButtonDropdownId: string = "SplitButtonDropDownId";
  private menuExecutionEvent: Event | undefined;

  static defaultProps = {
    directionalHintFixed: true,
    useTargetAsMinWidth: true,
  };

  static contextType: React.Context<AppContextType> = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: AppSplitButtonInternalProps) {
    super(props);

    this.state = {
      menuHasBeenOpened: false,
      isShadowRendering: false,
    };

    // Set a unique ID for the dropdown
    this.splitButtonDropdownId = uniqueDropdownId(this.props, this.props.id);
  }

  public render() {
    const {
      disabled,
      primaryDisabled,
      menuDefinition,
      toggled,
      toggleable,
      icon,
      iconColor,
      role,
      keytipProps,
      ariaRoleDescription,
      ariaChecked,
      title,
      styles,
      dataNav,
      shadowRenderMenu,
      deferredMenuDefinition,
      controlInMenuRendererFunction,
      preventMenuExecution,
      splitButtonAriaLabel,
      fullDescriptionLabel,
      delayUpdateFocusOnHover,
    } = this.props;
    const dataNavProp = dataNav
      ? {
          "data-nav": undefined,
        }
      : undefined;

    return (
      <ShadowRenderingContext.Provider
        value={getShadowRenderingContextValue(!!this.state.isShadowRendering)}
      >
        <AppThemeConsumer>
          {(theme: ITheme) => (
            <MenuAnchorContext.Provider
              value={
                deferredMenuDefinition
                  ? {
                      deferredMenuDefinition,
                      getMenuPropsFromMenuDefinition:
                        this.getMenuPropsFromMenuDefinitionFunction(
                          controlInMenuRendererFunction,
                          theme
                        ),
                    }
                  : DefaultMenuAnchorContext
              }
            >
              <AppBaseButton
                onClick={this.onExecute}
                ref={this.baseButton}
                split={true}
                menuAs={deferredMenuDefinition?.menuAs ?? AppContextualMenu}
                checked={toggled}
                toggle={toggleable}
                menuProps={{
                  ...this.getMenuPropsFromMenuDefinitionFunction(
                    controlInMenuRendererFunction,
                    theme
                  )(menuDefinition),
                  delayUpdateFocusOnHover,
                }}
                disabled={disabled}
                primaryDisabled={primaryDisabled}
                menuIconProps={iconNameToIconProps(
                  getDownChevronIcon(this.props)
                )}
                getClassNames={getClassNamesForButton(
                  theme,
                  styles,
                  icon !== undefined
                )}
                getSplitButtonClassNames={getSplitButtonClassNames(
                  theme,
                  styles,
                  icon !== undefined,
                  useMinimumTargetSizesIsEnabled(this.context)
                )}
                iconProps={iconNameToIconProps(icon, iconColor)}
                onAfterMenuDismiss={this.onAfterMenuDismiss}
                onPointerEnter={this.props.onPointerEnter}
                {...this._getLabel()}
                data-automation-type={
                  this.props.dataAutomationType || "AppSplitButton"
                }
                data-unique-id={dataUniqueId(this.props)}
                role={role}
                title={title}
                keytipProps={deriveButtonKeytip(
                  keytipProps,
                  this.keytipExecute
                )}
                ariaLabel={fullDescriptionLabel || this.props.label}
                splitButtonAriaLabel={splitButtonAriaLabel}
                ariaDescription={
                  this.props.ariaDescription
                    ? this.props.customTooltip
                      ? this.props.ariaDescription.trim() +
                        " " +
                        this.props.customTooltip.trim()
                      : this.props.ariaDescription.trim()
                    : this.props.customTooltip
                }
                aria-roledescription={ariaRoleDescription}
                aria-checked={ariaChecked}
                persistMenu={this.shouldPersistMenu()}
                renderPersistedMenuHiddenOnMount={getShadowRenderMenuValue(
                  shadowRenderMenu
                )} // This helps shadow rendering to incrementally mount menu items when hidden.
                data-nav={this.props.dataNav}
                menuTriggerKeyCode={this.props.menuTriggerKeyCode}
                primaryActionButtonProps={dataNavProp as any}
                onMenuClick={
                  preventMenuExecution
                    ? getPreventMenuClickCallback(this.executeMenuButton)
                    : this.setMenuExecutionEvent
                }
                {...(this.props.tabsterAttributes
                  ? { [tabsterAttributeName]: this.props.tabsterAttributes }
                  : {})}
              />
            </MenuAnchorContext.Provider>
          )}
        </AppThemeConsumer>
      </ShadowRenderingContext.Provider>
    );
  }

  public componentDidMount() {
    if (this.context.setTooltipElement && this.context.updateTooltipsHidden) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        htmlElement: this.baseButton.current?.buttonElement,
        controlId: this.props.id,
        controlLabel: getTooltipText(
          this.props.hideLabel,
          this.props.label,
          this.props.customTooltip
        ),
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: true,
        targetOverflowSetItem: !!checkExperiment(
          this.context,
          ribbonExperiments.tooltipsTargetOverflowSetItemEnabled
        ),
        location: this.props.location,
      });
      this.tooltipHost.registerTooltipHostActions();
    }

    this.tryInitiateShadowRendering();
  }

  public componentDidUpdate(prevProps: AppSplitButtonInternalProps) {
    this.tryInitiateShadowRendering();
    if (
      this.tooltipHost &&
      (prevProps.customTooltip !== this.props.customTooltip ||
        prevProps.label !== this.props.label ||
        prevProps.hideLabel !== this.props.hideLabel)
    ) {
      this.tooltipHost.updateTooltip(
        getTooltipText(
          this.props.hideLabel,
          this.props.label,
          this.props.customTooltip
        )
      );
    }
    // Ensure we never pass along a stale event
    this.menuExecutionEvent = undefined;
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
      this.tooltipHost = undefined;
    }
  }

  public openMenu = () => {
    this.baseButton?.current?.openMenu(true, false);
  };
  public dismissMenu = () => {
    this.baseButton?.current?.dismissMenu();
  };

  // Method outputs a function to generate full menu props from menuDefinition. In case of deferred menus, the output can be passed into the menu
  // so that it can generate full menu props using menu definition it fetches from the store.
  // The outer function should take in all parameters other than menuDefinition that can change the generation of the menuProps.
  private getMenuPropsFromMenuDefinitionFunction = memoizeFunction(
    (
      controlInMenuRendererFunction: ControlInMenuRendererFunction,
      theme: ITheme
    ) =>
      memoizeFunction(
        (menuDefinition: MenuDefinition | undefined): IContextualMenuProps => {
          const {
            location,
            directionalHintFixed,
            useTargetAsMinWidth,
            calloutWidth,
            menuHostId,
            terminalParentControlId,
            id,
          } = this.props;

          const baseMenuProps = menuDefinition
            ? convertMenuDefinitionToFabricMenuProps(
                {
                  menuDefinition,
                  controlInMenuRendererFunction,
                  location,
                  onSubMenuPositioned: this.onSubMenuReady,
                  customMenuItemStyles: this.props.menuItemStyles,
                  customMenuClassNames: this.props.menuClassNames,
                  onMenuPositioned: this.onCalloutReady,
                  theme,
                  terminalParentControlId: terminalParentControlId ?? id,
                  isExperimentalFeatureEnabled:
                    this.context.isExperimentalFeatureEnabled,
                },
                directionalHintFixed,
                this.context.appWindow
              )
            : emptyMenuProps;

          return {
            ...baseMenuProps,
            onMenuOpened: this.onMenuOpened,
            directionalHint: this.props.directionalHint,
            useTargetAsMinWidth,
            id: this.splitButtonDropdownId,
            calloutProps: {
              ...baseMenuProps.calloutProps,
              layerProps: { hostId: menuHostId },
              calloutWidth,
              preventDismissOnScroll: this.props.preventCalloutDismissOnScroll,
            },
          };
        }
      )
  );

  private _getLabel = (): Partial<IButtonProps> => {
    const {
      label,
      hideLabel,
      vertical,
      onRenderText,
      onPointerEnter,
      onMenuButtonPointerEnter,
    } = this.props;
    const labelProps = getLabelButtonProps(label, hideLabel);
    if (vertical) {
      return {
        splitButtonMenuProps: {
          menuIconProps: iconNameToIconProps(getDownChevronIcon(this.props)),
          text: labelProps.text,
          onRenderText,
          // Force the normal menu icon to not render since it will be replaced.
          onRenderMenuIcon: () => null,
          // overriding the function that gets invoked on Pointer Enter to show the L2 on Hover.
          onPointerEnter: onMenuButtonPointerEnter
            ? onMenuButtonPointerEnter
            : onPointerEnter,
        },
        menuIconProps: {},
        // Force the normal menu icon to not render since it will be replaced.
        onRenderMenuIcon: () => null,
      };
    }
    return {
      ...labelProps,
      onRenderText,
      splitButtonMenuProps: {
        onPointerEnter: onMenuButtonPointerEnter
          ? onMenuButtonPointerEnter
          : onPointerEnter,
      },
    };
  };

  private keytipExecute = (
    _executeTarget: HTMLElement,
    target: HTMLElement
  ) => {
    target && target.focus();
    this.openMenu();
  };

  private onExecute = (ev: React.MouseEvent<Button>) => {
    if (this.props.onExecute) {
      const { id, toggled, location, shouldTakeFocus } = this.props;
      this.props.onExecute({
        id,
        toggled,
        location,
        inputMethod: this.baseButton?.current?.getInputMethod(ev),
        shouldTakeFocus,
        triggerTime: ev && ev.timeStamp,
        event: ev.nativeEvent,
      });
    }
  };

  private onAfterMenuDismiss = () => {
    if (this.props.onAfterMenuDismiss) {
      this.props.onAfterMenuDismiss(this.props.id);
    }
    if (this.tooltipHost) {
      this.tooltipHost.hideAllTooltips(false);
    }
    if (this.tooltipHostMap) {
      this.unregisterTooltips();
    }
  };

  private unregisterTooltips() {
    this.tooltipHostMap.forEach((tooltipHost) => {
      tooltipHost.unregisterTooltipHostActions();
    });
    this.tooltipHostMap.clear();
  }

  private onMenuOpened = () => {
    if (!this.state.menuHasBeenOpened) {
      this.setState({
        menuHasBeenOpened: true,
      });
    }

    this.resetShadowRenderingAfterUserAction();
    this.executeMenuButton();

    if (this.tooltipHost) {
      this.tooltipHost.hideAllTooltips(true);
    }
  };

  private executeMenuButton = () => {
    if (this.props.onMenuButtonExecute) {
      const { id, location } = this.props;
      this.props.onMenuButtonExecute({
        id,
        location,
        inputMethod: this.baseButton?.current?.getInputMethod(),
        event: this.menuExecutionEvent,
      });
      this.menuExecutionEvent = undefined;
    }
  };

  private onSubMenuReady = (
    menuDefinition: MenuDefinition | undefined,
    flyoutMenuId?: string
  ) => {
    if (flyoutMenuId) {
      this.tooltipHostMap = setTooltipsOnMenuItems(
        menuDefinition,
        flyoutMenuId + "MenuId",
        this.tooltipHostMap,
        this.context.setTooltipElement,
        this.context.updateTooltipsHidden,
        true /** perform on next frame */,
        this.context.appWindow
      );
    }
  };

  private onCalloutReady = (menuDefinition: MenuDefinition | undefined) => {
    if (this.tooltipHostMap.size === 0) {
      this.tooltipHostMap = setTooltipsOnMenuItems(
        menuDefinition,
        this.splitButtonDropdownId,
        this.tooltipHostMap,
        this.context.setTooltipElement,
        this.context.updateTooltipsHidden,
        true /** perform on next frame */,
        this.context.appWindow
      );
    }
  };

  private shouldPersistMenu() {
    // this should only return true when menu has been opened at least once.
    return this.props.persistMenu && this.state.menuHasBeenOpened;
  }

  /**
   * This method is called on componentDidMount and componentDidUpdate.
   * If shadow rendering has already been initiated this is no-op.
   * If not, it tries to initiate it.
   * The reason why we have to call this multiple times on update is because
   * we do not want to initiate shadow rendering when the control is disabled.
   */
  private tryInitiateShadowRendering = () => {
    if (
      !this.initiatedShadowRendering &&
      getShadowRenderMenuValue(this.props.shadowRenderMenu) &&
      isIdleCallbackEnabled() &&
      !this.props.disabled &&
      this.state.menuHasBeenOpened === false
    ) {
      this.initiatedShadowRendering = true;
      window.requestIdleCallback(
        () => {
          const { id, location } = this.props;
          if (this.props.onMenuButtonExecute) {
            this.props.onMenuButtonExecute({
              id,
              location,
              isShadowRendering: true,
            });
          }
        },
        undefined /* options*/,
        // @ts-ignore Bug:5821196 - [TS 4.5.2 upgrade OOUI] Fix requestIdleCallback across OOUI
        `SplitButton-${this.props.id}-ShadowMenuOpen`
      );
      window.requestIdleCallback(
        () => {
          if (this.state.menuHasBeenOpened === false) {
            this.setState({
              isShadowRendering: true,
              menuHasBeenOpened: true,
            });
          }
        },
        undefined /* options*/,
        // @ts-ignore Bug:5821196 - [TS 4.5.2 upgrade OOUI] Fix requestIdleCallback across OOUI
        `SplitButton-${this.props.id}-StartShadowRendering`
      );
    }
  };

  /**
   * If a user opens the menu by clicking, keyboarding, through keytips
   * or through the API, the first thing we need to do is set shadow rendering
   * to false.
   */
  private resetShadowRenderingAfterUserAction = () => {
    if (this.state.isShadowRendering) {
      this.setState({
        isShadowRendering: false,
      });
    }
  };

  private setMenuExecutionEvent = (
    ev?:
      | React.MouseEvent<HTMLElement | BaseButton | Button>
      | React.KeyboardEvent<HTMLElement | BaseButton | Button>
  ) => {
    this.menuExecutionEvent = ev?.nativeEvent;
  };
}
